import {
  useMutation,
  useQueryClient,
  type UseMutateFunction,
  type UseMutateAsyncFunction,
} from '@tanstack/react-query';
import { companyUserService } from '../services/company-user.service';
import type { CompanyUser, UpdateCompanyUserRequest } from '../types/company-admin.types';
import { useAuthStore } from '@/features/auth/stores/authStore';

interface UpdateCompanyUserParams {
  userId: string;
  data: UpdateCompanyUserRequest;
}

export function useUpdateCompanyUser(): {
  updateUser: UseMutateFunction<CompanyUser, Error, UpdateCompanyUserParams>;
  updateUserAsync: UseMutateAsyncFunction<CompanyUser, Error, UpdateCompanyUserParams>;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
  reset: () => void;
} {
  const queryClient = useQueryClient();
  const getEffectiveCompanyId = useAuthStore((state) => state.getEffectiveCompanyId);

  const mutation = useMutation({
    mutationFn: ({ userId, data }: UpdateCompanyUserParams) =>
      companyUserService.updateUser(userId, data),
    onSuccess: () => {
      const companyId = getEffectiveCompanyId();
      queryClient.invalidateQueries({ queryKey: ['company-users', companyId] });
    },
  });

  return {
    updateUser: mutation.mutate,
    updateUserAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
}
